import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw, Trash2, Search, ArrowUpCircle, Download, Edit } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';
import * as XLSX from 'xlsx';

const AvancesSalaire = () => {
  const { filialeId, isConsolidated, currentFiliale } = useFiliale();
  const [avances, setAvances] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [paieEnCours, setPaieEnCours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAvance, setSelectedAvance] = useState(null);
  const [editingAvance, setEditingAvance] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [personnelSearch, setPersonnelSearch] = useState('');
  const [selectedPersonnel, setSelectedPersonnel] = useState([]);
  const [showPersonnelDropdown, setShowPersonnelDropdown] = useState(false);
  const [formData, setFormData] = useState({
    personnel_id: '',
    montant_avance: '',
    date_avance: ''
  });

  const devise = currentFiliale?.devise || 'FCFA';

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Récupérer la période de paie en cours
      let endpoint = '/api/paies/encours';
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        endpoint += `?filiale_id=${filialeId}`;
      }
      const paieRes = await http.get(endpoint).catch(() => ({ data: null }));
      setPaieEnCours(paieRes.data);

      if (paieRes.data) {
        // Récupérer les avances sur salaire
        let endpoint = `/api/avances?paie_id=${paieRes.data.idpaie}`;
        if (!isConsolidated && filialeId && filialeId !== 'all') {
          endpoint += `&filiale_id=${filialeId}`;
        }
        const avancesRes = await http.get(endpoint);
        setAvances(avancesRes.data || []);

        // Récupérer le personnel
        const personnelRes = await http.get('/api/personnel');
        const allPersonnel = Array.isArray(personnelRes.data?.data) ? personnelRes.data.data : [];
        
        // Filtrer par filiale si nécessaire
        if (!isConsolidated && filialeId && filialeId !== 'all') {
          const personnelInFiliale = allPersonnel.filter(p => p.filiale_id === parseInt(filialeId));
          setPersonnel(personnelInFiliale);
        } else {
          setPersonnel(allPersonnel);
        }
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filialeId, isConsolidated]);

  const handleCreate = () => {
    if (!paieEnCours) {
      alert('Merci d\'ouvrir une nouvelle période de paie');
      return;
    }
    setEditingAvance(null);
    setFormData({
      personnel_id: '',
      montant_avance: '',
      date_avance: ''
    });
    setSelectedPersonnel([]);
    setPersonnelSearch('');
    setShowPersonnelDropdown(false);
    setShowModal(true);
  };

  const handleEdit = (avance) => {
    setEditingAvance(avance);
    setFormData({
      personnel_id: avance.personnel_id,
      montant_avance: avance.montant_avance,
      date_avance: avance.date_avance
    });
    setSelectedPersonnel([avance.personnel_id]);
    setPersonnelSearch('');
    setShowPersonnelDropdown(false);
    setShowModal(true);
  };

  const togglePersonnelSelection = (personnelId) => {
    if (selectedPersonnel.includes(personnelId)) {
      setSelectedPersonnel(selectedPersonnel.filter(id => id !== personnelId));
    } else {
      setSelectedPersonnel([...selectedPersonnel, personnelId]);
    }
  };

  const handlePersonnelSearchChange = (e) => {
    setPersonnelSearch(e.target.value);
    setShowPersonnelDropdown(e.target.value.length > 0);
  };

  const handlePersonnelSelect = (personnel) => {
    if (!selectedPersonnel.includes(personnel.idpersonnel)) {
      setSelectedPersonnel([...selectedPersonnel, personnel.idpersonnel]);
    }
    setPersonnelSearch('');
    setShowPersonnelDropdown(false);
  };

  const removePersonnel = (personnelId) => {
    setSelectedPersonnel(selectedPersonnel.filter(id => id !== personnelId));
  };

  const handleDelete = (avance) => {
    setSelectedAvance(avance);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await http.delete(`/api/avances/${selectedAvance.idpersonnel_avance}`);
      alert('Avance sur salaire supprimée avec succès');
      setShowDeleteModal(false);
      loadData();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAvance) {
        // Modification : utiliser le premier employé sélectionné
        const personnelId = selectedPersonnel[0] || formData.personnel_id;
        await http.put(`/api/avances/${editingAvance.idpersonnel_avance}`, {
          ...formData,
          personnel_id: personnelId
        });
        alert('Avance sur salaire modifiée avec succès');
      } else {
        // Création : créer une avance pour chaque employé sélectionné
        for (const personnelId of selectedPersonnel) {
          await http.post('/api/avances', {
            personnel_id: personnelId,
            montant_avance: formData.montant_avance,
            date_avance: formData.date_avance,
            paie_id: paieEnCours.idpaie
          });
        }
        alert(`${selectedPersonnel.length} avance(s) sur salaire créée(s) avec succès`);
      }
      setShowModal(false);
      setEditingAvance(null);
      setSelectedPersonnel([]);
      loadData();
    } catch (error) {
      console.error('Erreur soumission:', error);
      alert('Erreur lors de la soumission');
    }
  };

  const handleExportExcel = () => {
    const data = avances.map(a => ({
      'Nom': a.nom,
      'Prénom': a.prenom,
      'Matricule': a.matricule,
      'Montant': a.montant_avance,
      'Date': a.date_avance
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Avances sur salaire');
    XLSX.writeFile(wb, 'avances_salaire.xlsx');
  };

  const filteredAvances = avances.filter(a =>
    a.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.matricule?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalMontant = filteredAvances.reduce((sum, a) => sum + parseFloat(a.montant_avance || 0), 0);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-gray-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Avances sur Salaire"
        subtitle="Gérer les avances sur salaire du personnel"
        actions={[
          {
            label: 'Ajouter',
            icon: Plus,
            onClick: handleCreate,
            primary: true
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: loadData,
            primary: false
          },
          {
            label: 'Exporter',
            icon: Download,
            onClick: handleExportExcel,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8">
        {!paieEnCours && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-yellow-800">Merci d'ouvrir une nouvelle période de paie pour gérer les avances sur salaire.</p>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, prénom ou matricule..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Liste des avances sur salaire</h3>
            <p className="text-sm text-gray-500 mt-1">{filteredAvances.length} avance(s) sur salaire</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom & Prénom</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matricule</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredAvances.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      Aucune avance sur salaire trouvée
                    </td>
                  </tr>
                ) : (
                  filteredAvances.map((a) => (
                    <tr key={a.idpersonnel_avance} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {a.nom} {a.prenom}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{a.matricule}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{a.montant_avance}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{a.date_avance}</td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleEdit(a)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(a)}
                            className="text-red-600 hover:text-red-800"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="bg-gray-50 border-t border-gray-200">
                <tr>
                  <td colSpan="2" className="px-6 py-3"></td>
                  <td className="px-6 py-3 font-bold text-gray-900 text-right">{totalMontant.toLocaleString()} {devise}</td>
                  <td colSpan="2" className="px-6 py-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Modal d'ajout/modification */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl border border-blue-100 flex flex-col max-h-[90vh]">
            <div className="mb-4 flex items-center justify-between border-b border-blue-100 pb-4 px-6">
              <h3 className="text-lg font-bold text-blue-900">
                {editingAvance ? 'Modifier une avance sur salaire' : 'Ajouter une avance sur salaire'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-900">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
              <div className="p-6 space-y-4 overflow-y-auto">
                {/* Recherche et sélection des employés */}
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Employé(s) <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher par nom, prénom ou matricule..."
                      value={personnelSearch}
                      onChange={handlePersonnelSearchChange}
                      onFocus={() => setShowPersonnelDropdown(personnelSearch.length > 0)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                    {showPersonnelDropdown && personnelSearch.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {personnel
                          .filter(p =>
                            p.nom?.toLowerCase().includes(personnelSearch.toLowerCase()) ||
                            p.prenom?.toLowerCase().includes(personnelSearch.toLowerCase()) ||
                            p.matricule?.toLowerCase().includes(personnelSearch.toLowerCase())
                          )
                          .slice(0, 10)
                          .map((p) => (
                            <div
                              key={p.idpersonnel}
                              onClick={() => handlePersonnelSelect(p)}
                              className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0"
                            >
                              <div className="text-sm font-medium text-gray-900">{p.nom} {p.prenom}</div>
                              <div className="text-xs text-gray-500">{p.matricule}</div>
                            </div>
                          ))}
                        {personnel.filter(p =>
                          p.nom?.toLowerCase().includes(personnelSearch.toLowerCase()) ||
                          p.prenom?.toLowerCase().includes(personnelSearch.toLowerCase()) ||
                          p.matricule?.toLowerCase().includes(personnelSearch.toLowerCase())
                        ).length === 0 && (
                          <div className="px-4 py-2 text-sm text-gray-500">Aucun employé trouvé</div>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Tags des employés sélectionnés */}
                  {selectedPersonnel.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedPersonnel.map((id) => {
                        const p = personnel.find(pers => pers.idpersonnel === id);
                        return p ? (
                          <div key={id} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-900 rounded-full text-sm">
                            {p.nom} {p.prenom}
                            <button
                              type="button"
                              onClick={() => removePersonnel(id)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              ✕
                            </button>
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Montant net de l'avance <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    value={formData.montant_avance}
                    onChange={(e) => setFormData({ ...formData, montant_avance: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Date de l'avance <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    value={formData.date_avance}
                    onChange={(e) => setFormData({ ...formData, date_avance: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 border-t border-blue-100 p-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={selectedPersonnel.length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingAvance ? 'Modifier' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de suppression */}
      {showDeleteModal && selectedAvance && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl border border-red-100">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-red-900">Supprimer l'avance sur salaire</h3>
              <p className="text-sm text-gray-600 mt-2">
                Êtes-vous sûr de vouloir supprimer l'avance sur salaire de {selectedAvance.nom} {selectedAvance.prenom} ?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Cette action est irréversible. Prière de générer à nouveau la paie de cet agent afin que ces nouveaux éléments soient pris en compte.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvancesSalaire;
